import React, { useState, useRef, useEffect } from "react";
import { Box, TextField } from "@mui/material";
import useStyles from "./styles";

const MDPinInput = ({
  length = 4,
  initialValue = "",
  secret = false,
  secretDelay = 500, // Unused but kept for compatibility
  type = "numeric",
  inputMode = "number",
  onChange = () => {},
  autoSelect = false,
  regexCriteria = /^[0-9]*$/,
  disabled = false,
  pinInputRef,
  error = false, // New prop to detect errors
}) => {
  const classes = useStyles();
  const [values, setValues] = useState(
    initialValue.padEnd(length, "").split("").slice(0, length)
  );
  const [displayValues, setDisplayValues] = useState(
    secret
      ? Array(length).fill("")
      : initialValue.padEnd(length, "").split("").slice(0, length)
  );
  const [readOnly, setReadOnly] = useState(true);
  const inputRefs = useRef([]);

  // Initialize refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
    console.log("[MDPinInput] Initialized with length:", length);
  }, [length]);

  // Expose clear method via ref
  useEffect(() => {
    if (pinInputRef) {
      pinInputRef.current = {
        clear: () => {
          setValues(Array(length).fill(""));
          setDisplayValues(Array(length).fill(""));
          if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
            inputRefs.current[0].select();
          }
          console.log("[MDPinInput] Cleared inputs");
        },
      };
    }
  }, [pinInputRef, length]);

  // Automatically clear on error
  useEffect(() => {
    if (error && pinInputRef.current) {
      pinInputRef.current.clear();
      console.log("[MDPinInput] Auto-cleared inputs due to error prop");
    }
  }, [error]);

  const handleChange = (index, value) => {
    if (!regexCriteria.test(value)) return;

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    // Update displayValues immediately
    const newDisplayValues = [...displayValues];
    newDisplayValues[index] = secret && value ? "•" : value;
    setDisplayValues(newDisplayValues);

    console.log(
      "[MDPinInput] Value changed at index",
      index,
      "to",
      value,
      "displayed as",
      newDisplayValues[index]
    );

    // Auto-focus next input
    if (value && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }

    // Trigger onChange if PIN is complete
    const pin = newValues.join("");
    if (pin.length === length) {
      console.log("[MDPinInput] PIN complete:", pin);
      onChange(pin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !values[index] && index > 0) {
      inputRefs.current[index - 1].focus();
      console.log("[MDPinInput] Backspace pressed, moved to index", index - 1);
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").slice(0, length);
    if (!regexCriteria.test(paste)) return;

    const newValues = paste.padEnd(length, "").split("").slice(0, length);
    setValues(newValues);

    // Update displayValues immediately
    const newDisplayValues = newValues.map((val) =>
      secret && val ? "•" : val
    );
    setDisplayValues(newDisplayValues);
    console.log(
      "[MDPinInput] Pasted PIN:",
      newValues.join(""),
      "displayed as",
      newDisplayValues.join("")
    );

    inputRefs.current[length - 1].focus();
    if (newValues.join("").length === length) {
      onChange(newValues.join(""));
    }
    e.preventDefault();
  };

  const handleFocus = () => {
    if (readOnly) {
      setReadOnly(false);
    }
  };

  return (
    <Box
      component="form"
      autoComplete="off"
      display="flex"
      gap={1}
      justifyContent="center"
      padding="10px 0 20px 0"
      onSubmit={(e) => e.preventDefault()}
    >
      {Array.from({ length }).map((_, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={displayValues[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={handleFocus}
          autoComplete="off"
          inputProps={{
            maxLength: 1,
            type: type === "numeric" ? "tel" : "text",
            name: `pin-verify-digit-${index}`,
            inputMode,
            pattern: type === "numeric" ? "[0-9]*" : undefined,
            autoComplete: "off",
            readOnly,
            "data-lpignore": "true",
            "data-form-type": "other",
          }}
          disabled={disabled}
          variant="outlined"
          className={classes.pinInputField}
          autoFocus={autoSelect && index === 0}
        />
      ))}
    </Box>
  );
};

export default MDPinInput;
